import React, { useMemo } from "react";
import {
  createNavigatorFactory,
  StackRouter,
  useLocale,
  useNavigationBuilder,
} from "@react-navigation/native";
import { StackView } from "@react-navigation/stack";

import { useLayoutMode } from "@/components/layout/layoutMode";
import { groupRuns } from "./routeGroups";

// createStackNavigator's navigator (same router, same StackView), except that on
// wide layouts consecutive screens of one group (getRouteGroup) reach StackView as
// a single card, drawn by `GroupComponent`: the event menu and the open setting
// side by side, or a centred modal. The navigation state is the same on every
// layout, so deep links, back buttons, the Android back button and browser history
// behave the same, and a phone renders exactly what createStackNavigator renders.

const GROUP_CARD_OPTIONS = {
  headerShown: false,
  // Keeps the dashboard under the card drawn, for the modal's blurred backdrop.
  presentation: "transparentModal",
  detachPreviousScreen: false,
  cardStyle: { backgroundColor: "transparent" },
  cardOverlayEnabled: false,
  cardShadowEnabled: false,
  gestureEnabled: false,
  // The group fades its modal in itself (AdaptiveGroup): fading the whole card
  // would put the backdrop's blur under a see-through parent, which browsers
  // don't blur through.
  animation: "none",
};

const GROUP_SCREEN_NAMES = {
  event: "__EventGroup",
  sheet: "__SheetGroup",
  test: "__TestGroup",
};

const groupState = ({
  state,
  descriptors,
  navigation,
  layoutMode,
  getRouteGroup,
  GroupComponent,
}) => {
  const runs = groupRuns(state.routes.slice(0, state.index + 1), getRouteGroup);
  if (!runs.some((run) => run.type)) return null;

  const routes = [];
  const groupDescriptors = { ...descriptors };

  for (const run of runs) {
    if (!run.type) {
      routes.push(...run.routes);
      continue;
    }
    const top = run.routes[run.routes.length - 1];
    // The name is not a screen of this navigator, so StackView never dispatches
    // actions for it (it would put this route into the real state).
    const route = {
      key: run.key,
      name: GROUP_SCREEN_NAMES[run.type],
      params: top.params,
    };
    const hasBackground = routes.length > 0;
    routes.push(route);
    groupDescriptors[route.key] = {
      route,
      navigation: descriptors[top.key].navigation,
      options: GROUP_CARD_OPTIONS,
      render: () => (
        <GroupComponent
          type={run.type}
          layoutMode={layoutMode}
          routes={run.routes}
          descriptors={descriptors}
          navigation={navigation}
          hasBackground={hasBackground}
        />
      ),
    };
  }

  return {
    state: { ...state, routes, index: routes.length - 1 },
    descriptors: groupDescriptors,
  };
};

function AdaptiveStackNavigator({
  id,
  initialRouteName,
  UNSTABLE_routeNamesChangeBehavior,
  children,
  layout,
  screenListeners,
  screenOptions,
  screenLayout,
  UNSTABLE_router,
  getRouteGroup,
  GroupComponent,
  ...rest
}) {
  const { direction } = useLocale();

  const { state, describe, descriptors, navigation, render } =
    useNavigationBuilder(StackRouter, {
      id,
      initialRouteName,
      UNSTABLE_routeNamesChangeBehavior,
      children,
      layout,
      screenListeners,
      screenOptions,
      screenLayout,
      UNSTABLE_router,
    });

  const layoutMode = useLayoutMode();
  const grouped = useMemo(
    () =>
      layoutMode === "phone" || !getRouteGroup || !GroupComponent
        ? null
        : groupState({
            state,
            descriptors,
            navigation,
            layoutMode,
            getRouteGroup,
            GroupComponent,
          }),
    [layoutMode, state, descriptors, navigation, getRouteGroup, GroupComponent]
  );

  return render(
    <StackView
      {...rest}
      direction={direction}
      state={grouped ? grouped.state : state}
      describe={describe}
      descriptors={grouped ? grouped.descriptors : descriptors}
      navigation={navigation}
    />
  );
}

export const createAdaptiveStackNavigator = createNavigatorFactory(
  AdaptiveStackNavigator
);
